<template>
  <v-card
    flat
    class="ma-3 pa-6"
  >
    <v-row align="center" class="pl-0 mb-6 pt-3 pr-3">
      <v-card-title>
        Import
      </v-card-title>
      <v-spacer></v-spacer>
      <v-btn
        class="ml-4"
        depressed
        color="secondary"
        @click="showUploadDialog()"
      >
        <v-icon
          small
          left
        >fas fa-upload</v-icon>
        Upload
      </v-btn>
    </v-row>

    <v-expansion-panels
      v-if="isImport"
      v-model="panels"
      accordion
      multiple
      flat
    >

      <!-- Product -->
      <v-expansion-panel>
        <v-expansion-panel-header color="background">Product
          <span v-if="errors.product" class="text-right mr-16"><v-icon color="error" small>fas fa-exclamation</v-icon></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="my-5">

          <v-row dense>
            <v-col cols="6">
              <v-radio-group
                v-model="model.is_new_product"
                @change="updateProductColors()"
                row
              >
                <v-radio
                  label="Nieuw product"
                  :value="true"
                ></v-radio>
                <v-radio
                  label="Bestaand product"
                  :value="false"
                ></v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                v-if="!model.is_new_product"
                outlined
                v-model="model.product_id"
                :error-messages="errors.product_id"
                :items="products"
                :loading="isSearching"
                :search-input.sync="filter.query"
                label="Search product..."
                item-text="name"
                item-value="id"
                hide-no-data
                hide-selected
                no-filter
                @keyup="productSearch()"
                @change="updateProductColors()"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row v-if="model.is_new_product">
            <v-col cols="8">
              <v-text-field
                outlined
                dense
                v-model="model.description"
                :error-messages="errors.description"
                label="Description"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                outlined
                dense
                v-model="model.code"
                :error-messages="errors.code"
                label="Code"
              ></v-text-field>
            </v-col>
            <v-col cols="1"></v-col>
            <v-col cols="12" class="pt-0">
              <v-simple-table>
                <tbody>
                  <tr
                    v-for="(id, brand) in model.brands"
                    :key="'brand'+brand"
                  >
                    <td class="col-2">Brand</td>
                    <td class="col-6">{{ brand }}</td>
                    <td class="col-3">
                      <v-select
                        outlined
                        v-model="model.brands[brand]"
                        :error-messages="errors['brands.'+brand]"
                        :items="brands"
                        item-text="name"
                        item-value="id"
                        dense
                        hide-details
                        clearable
                        clear-icon="fas fa-times"
                      ></v-select>
                    </td>
                    <td class="col-1 text-right">
                      <v-btn
                        icon
                        @click="showBrandDialog(brand)"
                      ><v-icon small>fas fa-plus</v-icon></v-btn>
                    </td>
                  </tr>
                  <tr
                    v-for="(id, category) in model.categories"
                    :key="'category'+category"
                  >
                    <td class="col-2">Category</td>
                    <td class="col-6">{{ category }}</td>
                    <td class="col-3">
                      <v-select
                        outlined
                        v-model="model.categories[category]"
                        :error-messages="errors['categories.'+category]"
                        :items="categories"
                        item-text="name"
                        item-value="id"
                        dense
                        hide-details
                        clearable
                        clear-icon="fas fa-times"
                      ></v-select>
                    </td>
                    <td class="col-1 text-right">
                      <v-btn
                        icon
                        @click="showCategoryDialog(category)"
                      ><v-icon small>fas fa-plus</v-icon></v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>

          <brand-dialog
            v-model="brandDialog"
            :attributes="brand"
            :errors="brandErrors"
            @save="saveBrand"
          ></brand-dialog>

          <category-dialog
            v-model="categoryDialog"
            :attributes="category"
            :errors="categoryErrors"
            :categories="categories"
            @save="saveCategory"
          ></category-dialog>

        </v-expansion-panel-content>

      </v-expansion-panel>


      <!-- Colors -->
      <v-expansion-panel>
        <v-expansion-panel-header color="background">Colors
          <span v-if="errors.colors" class="text-right mr-16"><v-icon color="error" small>fas fa-exclamation</v-icon></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="my-5">
          <v-simple-table>
            <tbody>
              <tr
                v-for="(id, color) in model.colors"
                :key="'color'+color"
              >

                <td class="col-4">{{ color }}</td>
                <td class="col-1">
                  <v-switch
                    v-if="!model.is_new_product"                  
                    v-model="showAllColors[color]"
                    inset
                    dense
                    label="All"
                    @change="val => switchAllColors(color, val)"
                  ></v-switch>
                </td>

                <td class="col-3">
                  <v-select
                    v-if="model.is_new_product || showAllColors[color]"
                    outlined
                    v-model="selectedColorFamilies[color]"
                    :items="colorFamilies"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    clearable
                    clear-icon="fas fa-times"
                    @change="model.colors[color] = null"
                  ></v-select>
                </td>

                <td class="col-3">
                  <v-select
                    v-if="!model.is_new_product && !showAllColors[color]"
                    outlined
                    v-model="model.colors[color]"
                    :error-messages="errors['colors.'+color]"
                    :items="productColors"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    clearable
                    clear-icon="fas fa-times"
                    @change="updateColor(color)"
                  ></v-select>
                  <v-select
                    v-if="model.is_new_product || showAllColors[color]"
                    outlined
                    v-model="model.colors[color]"
                    :error-messages="errors['colors.'+color]"
                    :items="availableColors[color]"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    clearable
                    clear-icon="fas fa-times"
                    @change="updateColor(color)"
                  ></v-select>
                </td>

                <td class="col-1 text-right">
                  <v-btn
                    v-if="model.is_new_product || showAllColors[color]"
                    icon
                    @click="showColorDialog(color)"
                  ><v-icon small>fas fa-plus</v-icon></v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <color-dialog
            v-model="colorDialog"
            :attributes="color"
            :errors="colorErrors"
            :families="colorFamilies"
            @save="saveColor"
          ></color-dialog>

        </v-expansion-panel-content>

      </v-expansion-panel>

      <!-- Sizes -->
      <v-expansion-panel v-if="model.has_size">
        <v-expansion-panel-header color="background">Sizes
          <span v-if="errors.sizes" class="text-right mr-16"><v-icon color="error" small>fas fa-exclamation</v-icon></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="my-5">
          <v-simple-table>
            <tbody>
              <tr
                v-for="(id, size) in model.sizes"
                :key="'size'+size"
              >
                <td class="col-5">{{ size }}</td>
                <td class="col-3">
                  <v-select
                    outlined
                    v-model="selectedSizeFamilies[size]"
                    :items="sizeFamilies"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    clearable
                    clear-icon="fas fa-times"
                    @change="model.sizes[size] = null"
                  ></v-select>
                </td>
                <td class="col-3">
                  <v-select
                    outlined
                    v-model="model.sizes[size]"
                    :error-messages="errors['sizes.'+size]"
                    :items="availableSizes[size]"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    clearable
                    clear-icon="fas fa-times"
                    @change="updateSize(size)"
                  ></v-select>
                </td>
                <td class="col-1 text-right">
                  <v-btn
                    icon
                    @click="showSizeDialog(size)"
                  ><v-icon small>fas fa-plus</v-icon></v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <size-dialog
            v-model="sizeDialog"
            :attributes="size"
            :errors="sizeErrors"
            :families="sizeFamilies"
            @save="saveSize"
          ></size-dialog>

        </v-expansion-panel-content>

      </v-expansion-panel>

    </v-expansion-panels>

    <v-card-actions class="mx-n2 my-8">
      <v-spacer></v-spacer>
      <v-btn
        v-if="isImport"
        color="primary"
        class="px-5"
        large
        depressed
        @click="importProduct()"
      >Import</v-btn>
    </v-card-actions>


    <v-dialog
      v-model="uploadDialog"
      max-width="600"
      persistent
    >
      <v-card>
        <v-toolbar color="primary" dark flat short class="px-2">
          <v-toolbar-title>Upload CSV</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn @click="uploadDialog = false" icon><v-icon>fas fa-times</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="mt-12">
          <v-form
            ref="uploadForm"
            @submit.prevent
          >
            <v-row>
              <v-col cols="10">

                <file-drop-input
                  outlined
                  v-model="file"
                  :error-messages="errors.data_url"
                  clear-icon="fas fa-times"
                  accept="text/csv"
                  label="File input"
                ></file-drop-input>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  outlined
                  v-model="model.csv_separator"
                  :error-messages="errors.csv_separator"
                  label="Separator"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-select
              v-if="errors.supplier_id"
              outlined
              clearable
              clear-icon="fas fa-times"
              v-model="model.supplier_id"
              :items="suppliers"
              item-text="name"
              item-value="id"
              :error-messages="errors.supplier_id"
            ></v-select>

            <v-checkbox
              v-model="model.has_size"
              :error-message="errors.has_size"
              label="Use sizes"
            ></v-checkbox>

          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-8">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="px-5"
            large
            outlined
            depressed
            @click="uploadDialog = false"
          >Cancel</v-btn>
          <v-btn
            color="primary"
            class="px-5"
            large
            depressed
            @click="uploadFile()"
          >Upload</v-btn>
        </v-card-actions>

      </v-card>

    </v-dialog>

  </v-card>
  
</template>

<script>
/* eslint-disable */
import { inject, ref, computed } from 'vue'
import { readFileAsDataUrl } from '@/utils'
import FileDropInput from '@/components/FileDropInput'
import ColorDialog from '@/components/ColorDialog'
import SizeDialog from '@/components/SizeDialog'
import BrandDialog from '@/components/BrandDialog'
import CategoryDialog from '@/components/CategoryDialog'
import { cloneDeep } from 'lodash'


export default {
  components: {
    FileDropInput,
    ColorDialog,
    SizeDialog,
    BrandDialog,
    CategoryDialog,
  },
  setup() {
    const api = inject('api')

    const panels = ref([])

    const uploadDialog = ref(false)
    const colorDialog = ref(false)
    const sizeDialog = ref(false)
    const brandDialog = ref(false)
    const categoryDialog = ref(false)

    const file = ref()
    const selectedColorFamilies = ref({})
    const selectedSizeFamilies = ref({})
    const showAllColors = ref({})

    const importModel = api.model('products/import', {
      attributes: {
        scenario: 'default',
        is_new_product: true,
        has_size: true,
        product_id: null,
        colors: {},
        sizes: {},
        brands: {},
        categories: {},
      },
      afterSave: () => {
        updateColors()
        updateSizes()
      },
    })
    const { attributes: model, oldAttributes: oldModel, errors } = importModel

    const colorCollection = api.collection('colors').all()
    const supplierCollection = api.collection('suppliers').all()
    const colorFamiliesCollection = api.collection('color-families').all()
    const sizeCollection = api.collection('sizes').all()
    const sizeFamilyCollection = api.collection('size-families').all()
    const brandCollection = api.collection('brands').all()
    const categoryCollection = api.collection('categories').all()
    const productColorCollection = api.collection()

    const { items: suppliers } = supplierCollection
    const { items: colors } = colorCollection
    const { items: colorFamilies } = colorFamiliesCollection
    const { items: sizes } = sizeCollection
    const { items: sizeFamilies } = sizeFamilyCollection
    const { items: brands } = brandCollection
    const { items: categories } = categoryCollection
    const { items: productColors } = productColorCollection

    const isImport = computed(() => model.value.scenario === 'import')

    // debug
    // importModel.setAttributes({
    //   data_url: "data:application/vnd.ms-excel;base64,IkJyYW5kIjsiU0tVIjsiUHJvZHVjdE5hbWUiOyJDb2xvdXIiOyJXaGl0ZS9Db2xvciI7IlNpemUiOyJTaXplUmFuZ2UiOyJNYXN0ZXIiOyJTdWJjYXRlZ29yeSI7IkdlbnJlIjsiVWl0dm9lcmluZyI7IlNsZWV2ZV9TdHlsZSI7IlByaWNlIjsiUmVjb21tZW5kZWRTZWxsaW5nUHJpY2UiCiJGcnVpdCBvZiB0aGUgbG9vbSI7IjZNMDE0VjMwTSI7IkZPVEwgRmFjZSBNYXNrIDUtcGFjayI7IldoaXRlIjsiQWxsIENvbG9ycyI7Ik9uZSBzaXplIjsifE9uZSBzaXplfCI7IjZNMDE0ViI7Ik1pc2NlbGxhbmVvdXMiOyJNZW4vVW5pIjsiIjsiIjsiNSw1MSI7IjE0LDkiCiJGcnVpdCBvZiB0aGUgbG9vbSI7IjZNMDE0VjM2TSI7IkZPVEwgRmFjZSBNYXNrIDUtcGFjayI7IkJsYWNrIjsiQWxsIENvbG9ycyI7Ik9uZSBzaXplIjsifE9uZSBzaXplfCI7IjZNMDE0ViI7Ik1pc2NlbGxhbmVvdXMiOyJNZW4vVW5pIjsiIjsiIjsiNSw1MSI7IjE0LDki",
    //   csv_separator: ';' })
    // importModel.create()

    // panels
    function showPanels(errorsOnly = false) {
      panels.value = []
      ;['product', 'colors', 'sizes'].forEach((panel, index) => {
        if (!errorsOnly || errors.value[panel]) {
          panels.value.push(index)
        }
      })
    }


    // upload
    function showUploadDialog() {
      importModel.clear().setAttributes({ csv_separator: ';', supplier_id: null })
      file.value = null
      uploadDialog.value = true
    }

    function uploadFile() {
      readFileAsDataUrl(file.value).then(url => {
        importModel.setAttributes({ data_url: url })
      }).catch(() => {
        importModel.setAttributes({ data_url: null })
      }).finally(() => {
        importModel.create().then(() => {
          uploadDialog.value = false

          showPanels()
        })
      })
    }

    // colors
    const colorModel = api.model('colors/:id')
    const { attributes: color, errors: colorErrors } = colorModel

    function showColorDialog(color) {
      colorDialog.value = true
      colorModel.clear().setAttributes({ name: color, _name: color })
    }

    function saveColor() {
      colorModel.save().then(() => {
        colorDialog.value = false
        colorCollection.all(() => {
          let name = colorModel.getAttribute('_name')
          if (name) {
            model.value.colors[name] = colorModel.getAttribute('id')
            updateColor(name)
          }
        })
      })
    }

    const availableColors = computed(() => {
      let availableColors = {}
      console.log('availableColors')
      Object.keys(model.value.colors).forEach(color => {
        availableColors[color] = colors.value.filter(item => !selectedColorFamilies.value[color] || item.color_family_id == selectedColorFamilies.value[color])
      })
      return availableColors
    })

    function updateColor(color) {
      console.log('updateColor', color)
      let item = colors.value.find(item => item.id == model.value.colors[color])
      selectedColorFamilies.value = Object.assign({}, selectedColorFamilies.value, { [color]: item ? item.color_family_id : null })
      if (showAllColors.value[color] === undefined) {
        showAllColors.value = Object.assign({}, showAllColors.value, { [color]: false })
      }
    }

    function updateColors() {
      Object.keys(model.value.colors).forEach(color => updateColor(color))
    }

    function updateProductColors() {
      if (!importModel.getAttribute('is_new_product') && importModel.getAttribute('product_id')) {
        productColorCollection.setPath(`products/${importModel.getAttribute('product_id')}/colors`).all(() => {
          Object.keys(model.value.colors).forEach(color => switchAllColors(color, false))
        })
      } else {
        productColorCollection.clear()
      }
    }

    function switchAllColors(color, value) {
      if (value) {
        model.value.colors[color] = oldModel.value.colors[color]
        updateColor(color)
      } else {
        let productColor = productColors.value.find(item => item.id == oldModel.value.colors[color])
        model.value.colors[color] = productColor ? productColor.id : null
      }
    }

    // sizes
    const sizeModel = api.model('sizes/:id')
    const { attributes: size, errors: sizeErrors } = sizeModel

    function showSizeDialog(size) {
      sizeDialog.value = true
      sizeModel.clear().setAttributes({ name: size, _name: size })
    }

    function saveSize() {
      sizeModel.save().then(() => {
        sizeDialog.value = false
        sizeCollection.all(() => {
          let name = sizeModel.getAttribute('_name')
          if (name) {
            model.value.sizes[name] = sizeModel.getAttribute('id')
          }
        })
      })
    }

    const availableSizes = computed(() => {
      let availableSizes = {}
      console.log('availableSizes')
      Object.keys(model.value.sizes).forEach(size => {
        availableSizes[size] = sizes.value.filter(item => !selectedSizeFamilies.value[size] || item.size_family_id == selectedSizeFamilies.value[size])
      })
      return availableSizes
    })

    function updateSize(size) {
      console.log('updateSize')
      let item = sizes.value.find(item => item.id == model.value.sizes[size])
      selectedSizeFamilies.value = Object.assign({}, selectedSizeFamilies.value, { [size]: item ? item.size_family_id : null })
    }

    function updateSizes() {
      Object.keys(model.value.sizes).forEach(size => updateSize(size))
    }

    // brands
    const brandModel = api.model('brands/:id')
    const { attributes: brand, errors: brandErrors } = brandModel

    function showBrandDialog(brand) {
      brandDialog.value = true
      brandModel.clear().setAttributes({ name: brand, _name: brand })
    }

    function saveBrand() {
      brandModel.save().then(() => {
        brandDialog.value = false
        brandCollection.all()
        let name = brandModel.getAttribute('_name')
        if (name) {
          model.value.brands[name] = brandModel.getAttribute('id')
        }
      })
    }
    
    // categories
    const categoryModel = api.model('categories/:id')
    const { attributes: category, errors: categoryErrors } = categoryModel

    function showCategoryDialog(category) {
      categoryDialog.value = true
      categoryModel.clear().setAttributes({ name: category, _name: category })
    }

    function saveCategory() {
      categoryModel.save().then(() => {
        categoryDialog.value = false
        categoryCollection.all()
        let name = categoryModel.getAttribute('_name')
        if (name) {
          model.value.categories[name] = categoryModel.getAttribute('id')
        }
      })
    }

    // import
    function importProduct() {
      importModel.create().then(() => {
        importModel.clear()
      }).catch(() => {
        showPanels(true)
      })
    }

    const productCollection = api.collection('products', {
      params: {
        expand: 'category,brand',
      },
      filter: {
        query: 'query:code,unique_name',
      },
    })

    const { items: products, filter, isSearching } = productCollection

    const productSearch = productCollection.debounceSearch(300) 

    function dropFiles(e) {
      console.log('dropFiles', e)
    }


    return {
      panels,
      isImport,

      uploadDialog,
      showUploadDialog,
      uploadFile,

      showColorDialog,
      colorDialog,
      color,
      colorErrors,
      saveColor,

      showSizeDialog,
      sizeDialog,
      size,
      sizeErrors,
      saveSize,

      showBrandDialog,
      brandDialog,
      brand,
      brandErrors,
      saveBrand,

      showCategoryDialog,
      categoryDialog,
      category,
      categoryErrors,
      saveCategory,

      importProduct,

      file,

      model,
      errors,
      selectedColorFamilies,
      selectedSizeFamilies,

      suppliers,
      colorFamilies,
      colors,
      sizeFamilies,
      sizes,
      brands,
      categories,

      productColors,
      showAllColors,
      switchAllColors,

      availableColors,
      updateColor,

      availableSizes,
      updateSize,

      products,
      filter,
      isSearching,
      productSearch,
      updateProductColors,

      dropFiles,
    }

  },
}
</script>


<style scoped>

:deep(.v-data-table table tbody tr:not(.v-data-table__empty-wrapper):hover) {
  /* cursor: pointer; */
}

</style>
